import { OAuthCredentialsService } from '@tableau/oauth-credentials-service';
import { ButtonType, ButtonWidget, TextLinkKind, TextLinkWidget } from '@tableau/widgets';
import React from 'react';
import _ from 'underscore';

import { ReactModalService } from '~commonInfra/dialogs/ReactModalService';
import ConnectToSalesforceDropdown, {
    ConnectToSalesforce
} from '~src/app/spaces/IntegrationsSettings/components/ConnectToSalesforceDropdown';
import { SlackIntegrationSettings } from '~src/app/spaces/IntegrationsSettings/slackConnectivity/SlackIntegrationSettings';
import {
  integrationSettingConnectButton,
  integrationSettingsContainer,
  integrationSpaceAuthenticateContainer,
  integrationSpaceContainer,
  integrationsSettingsPageHeader,
  salesforceSettingsAfterConnecting,
  salesforceSettingsGroupContainer,
  salesforceSettingsGroupName,
  salesforceSettingsParagraph} from '~src/app/spaces/IntegrationsSettings/styles/IntegrationsSettingsStyles';
import { SalesforceIntegrationAddCredentialsButtonClickEvent } from '~src/app/spaces/IntegrationsSettings/telemetry/IntegrationsSettingsTelemetry';
import { Messages } from '~src/codegen/Localize';
import { HelpService } from '~src/commonInfra/services/HelpService';
import { OAuthCredentials } from '~src/connectivity/resources/OAuthCredentials';
import { SessionManagementService } from '~src/sessionManagement/SessionManagementService';
import { SpacesSiteUrls } from '~src/spacesPlatform/components/spaceComponents/SpacesSiteUrls';
import { ServerSettingsSubspaceName } from '~src/spacesPlatform/state/SpaceInfo';
import { FeatureFlags } from '~src/ts/FeatureFlags';
import { Filter } from '~src/ts/helpers/Filter';
import { SliceableHelper } from '~src/ts/helpers/SliceableHelper';
import { ResourceQuery } from '~src/ts/resources/ResourceQuery';
import { IOAuthCredential, OAuthCredentialTypes } from '~src/ts/ServerApi/types/IOAuthCredential';
import { SiteRoles } from '~src/ts/ServerApi/types/ISiteRole';
import {
    UploadBundlesDialogOpts
} from '~src/uploadBundles/components/ConnectedUploadBundlesDialog';

export const IntegrationsSubspaceName: ServerSettingsSubspaceName = 'integrations';

export interface IntegrationsSiteSettingsSubspaceOwnProps {
  readonly siteName: string;
  readonly urls: SpacesSiteUrls;
  readonly server: SessionManagementService;
  readonly isSessionMultiSite: boolean;
  readonly isSessionServerAdmin: boolean;
  readonly isOnlineEnvironment: boolean;
  readonly userRole: String; // Prop added due to TFS 1320485
}

export type IntegrationsSiteSettingsSubspaceProps = IntegrationsSiteSettingsSubspaceOwnProps;

export interface IntegrationsSiteSettingsSubspaceState {
  readonly credentials: IOAuthCredential[];
}

export class IntegrationsSiteSettingsSubspaceComponent extends React.PureComponent<IntegrationsSiteSettingsSubspaceProps, IntegrationsSiteSettingsSubspaceState> {

  public constructor(props: IntegrationsSiteSettingsSubspaceProps) {
    super(props);
    this.state = {
      credentials: null,
    };
  }

  public async componentDidMount() {
    await this.getOauthCredentials();
  }

  async getOauthCredentials() {
    const credentialsQuery: ResourceQuery<IOAuthCredential> =
      OAuthCredentials.all().addFilter(Filter.fieldEq('type', OAuthCredentialTypes.Salesforce));

    const result = await SliceableHelper.fetchAll(credentialsQuery);
    if (_.isEmpty(result.errors)) {
      this.setState({credentials: result.items});
    }
  }

  public handleSelectedCredentials = (credentialId: number): void => {
    ReactModalService.openCustom(UploadBundlesDialogOpts(credentialId));
  };

   public handleConnectToSalesforce = (): void => {
     SalesforceIntegrationAddCredentialsButtonClickEvent();
     OAuthCredentialsService.AddCredential({
       bypassBeforeConnectingDialog: true,
       connectionType: OAuthCredentialTypes.Salesforce,
       onCancel: () => {
       }, // TODO: probably dont need to do anything.
       onCredentialAdded: this.handleOnCredentialsAdded,
       shouldKeepWindowOpen: false,
       sendEventFn: () => {
       },
       sessionLanguage: this.props.server.sessionLanguage(),
       sessionServerVersionBuild: this.props.server.sessionServerVersion().build,
       sessionServerVersionPlatform: this.props.server.sessionServerVersion().platform,
       connectionTypeDisplay: Messages.salesforce(),
       helpServiceExternalVersion: undefined,
       supportsGenericAuth: false,
     });
   };

  // credentialProperties is needed because we use it as a callback that requires it.
  handleOnCredentialsAdded = async (credentialId: string, _credentialProperties: [string, any]) => {
    this.handleSelectedCredentials(parseInt(credentialId));
    await this.getOauthCredentials(); // refresh state so button migrates to dropdown.
  }

  public render() {
    return (
      <div className={integrationSpaceContainer}>
        { this.shouldRenderSalesforceIntegrationSettings() && <div className={integrationSettingsContainer}>
          <h2 className={integrationsSettingsPageHeader}>{FeatureFlags.salesforceBundlesUIImprovements? Messages.connectToSalesforce_SalesforceDataSourcesAndWorkbooksHeader()
           : Messages.salesforce()}</h2>
          {FeatureFlags.salesforceBundlesUIImprovements? this.connectToSalesforceDataSegment() : this.connectToSalesforceSegment()}
          {FeatureFlags.salesforceBundlesUIImprovements? this.salesforceAuthenticationSegment() : this.authenticationSegment()}
        </div> }
        { FeatureFlags.userNotificationSlackChannel && <div className={integrationSettingsContainer}>
          <SlackIntegrationSettings />
        </div>}
      </div>
    );
  }

  private shouldRenderSalesforceIntegrationSettings = (): boolean => {
    const userAuthorized = this.props.userRole === SiteRoles.ServerAdministrator || this.props.userRole === SiteRoles.SiteAdministratorCreator;
    return FeatureFlags.publishingSalesforceBundlesEnabled && userAuthorized;
  }

  public connectToSalesforceSegment = (): JSX.Element => {
    return <div className={salesforceSettingsGroupContainer}>
      <h3 className={salesforceSettingsGroupName}>
        {Messages.connectToSalesforce_DataSourcesAndWorkbooksHeader()}
      </h3>
      <p className={salesforceSettingsParagraph}>
        {Messages.connectToSalesforce_DataSourcesAndWorkbooksFirstParagraph()}
      </p>
      <p className={salesforceSettingsParagraph}>
        {Messages.connectToSalesforce_DataSourceAndWorkbooksEmbeddedCredentials()}
      </p>
      {this.getButton(this.state.credentials)}
      <p className={salesforceSettingsParagraph}>
        {Messages.connectToSalesforce_DataSourcesAndWorkbooksSecondParagraph()}
        {this.scheduleRefreshesTextWidget()}
      </p>
    </div>;
  }

  private scheduleRefreshesTextWidget = (): JSX.Element => {
    return <TextLinkWidget style={{display:'inline-block'}}
    testId={`${ConnectToSalesforce}-schedule-refreshes-learn-more`}
    kind={TextLinkKind.Standard}
    href={HelpService.getHelpUrlByCategory('schedule-refreshes')}
    target={'_blank'}
    rel={'noreferrer noopener'}>
    {Messages.connectToSalesforce_LearnMore()}
    </TextLinkWidget>;
  }

  public connectToSalesforceDataSegment = (): JSX.Element => {
    return <div className={salesforceSettingsGroupContainer}>
      {<p className={salesforceSettingsParagraph}>
        {Messages.connectToSalesforce_DataSourcesAndWorkbooksExploreData()}
        <TextLinkWidget
          testId={`${ConnectToSalesforce}-create-workbooks-learn-more`}
          kind={TextLinkKind.Standard}
          href={HelpService.getHelpUrlByCategory('server-connect-salesforce')}
          target={'_blank'}
          rel={'noreferrer noopener'}>
          {Messages.connectToSalesforce_LearnMore()}
          </TextLinkWidget>
      </p>}
      <h3 className={salesforceSettingsGroupName}>
      {Messages.connectToSalesforce_getStarted()}
      </h3>
      <p className={salesforceSettingsParagraph}>
        {Messages.connectToSalesforce_DataSourcesAndWorkbooksParagraph()}
      </p>
      <p className={salesforceSettingsParagraph}>
        {Messages.connectToSalesforce_DataSourceAndWorkbooksEmbedCredentials()}
      </p>
      {this.getButton(this.state.credentials)}
      <h3 className={salesforceSettingsAfterConnecting}>
      {Messages.connectToSalesforce_afterConnecting()}
      </h3>
      <div className={salesforceSettingsParagraph} style={{paddingBottom:0}}>
        <ul style={{paddingLeft:22, paddingBottom:0}}>
          <li>{Messages.connectToSalesforce_DataSourcesAndWorkbooksAfterConnectingBulletOne()}</li>
          <li style={{whiteSpace: 'nowrap'}}>{Messages.connectToSalesforce_DataSourcesAndWorkbooksAfterConnectingBulletTwo()}
          {this.scheduleRefreshesTextWidget()}
          </li>
        </ul>
      </div>
    </div>;
  }

  public getButton = (credentials: IOAuthCredential[]): JSX.Element => {
    if (credentials && (credentials.length > 0)) {
      return <ConnectToSalesforceDropdown
        credentials={credentials}
        handleSelectedItem={this.handleSelectedCredentials}
        handleAddNewCredentials={this.handleConnectToSalesforce}
      />;
    }

    return <>
      <ButtonWidget
        testId={`${ConnectToSalesforce}-data`}
        className={integrationSettingConnectButton}
        id={`${ConnectToSalesforce}-data-button`}
        handleClick={this.handleConnectToSalesforce}
        buttonType={ButtonType.Outline}
      >
        {FeatureFlags.salesforceBundlesUIImprovements? Messages.connectToSalesforce_Title() : Messages.connectToSalesforce_ConnectToSalesforceButtonTag()}
      </ButtonWidget>
    </>;
  }

  public authenticationSegment = (): JSX.Element => {
    if (this.props.isOnlineEnvironment) {
      return <div className={salesforceSettingsGroupContainer}>
        <h3 className={salesforceSettingsGroupName}>
          {Messages.connectToSalesforce_AuthenticationHeader()}
        </h3>
        {this.authenticationBody()}
      </div>;
    }
  }

  public salesforceAuthenticationSegment = (): JSX.Element => {
    if (this.props.isOnlineEnvironment) {
      return <div>
        <div className={integrationSpaceAuthenticateContainer}>
        <h2 className={integrationsSettingsPageHeader}>
          {Messages.connectToSalesforce_salesforceAuthenticationAndIdentity()}
        </h2>
        </div>
        <div className={salesforceSettingsGroupContainer}>
        {this.authenticationBody()}
      </div>
      </div>;
    }
  }

  private authenticationBody = (): JSX.Element => {
    return <p className={salesforceSettingsParagraph}>
          {Messages.connectToSalesforce_AuthenticationBody()}
          <TextLinkWidget
            testId={`${ConnectToSalesforce}-authentication`}
            kind={TextLinkKind.Standard}
            target={'_self'}
            href={this.props.urls.settingsAuthenticationUrl()}
          >
            {Messages.connectToSalesforce_AuthenticationLink()}
          </TextLinkWidget>
        </p>;
  }
}
